import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { formatINR } from '@/utils/format';

interface ParentItem {
  id: string;
  name: string;
  code: string;
  hsnCode?: string;
  taxRate?: number;
  categoryId?: string;
  categoryName?: string;
  unitId?: string;
  unitName?: string;
  itemType?: string;
  isBatchTracked?: boolean;
  isSerialTracked?: boolean;
  purchaseRate?: number;
  saleRate?: number;
  mrp?: number;
  variantCount?: number;
  bomId?: string | null;
  bomVersion?: string | null;
  minStockLevel?: number;
  productionUnit?: string | null;
  standardCost?: number;
  reorderPoint?: number;
  reorderQty?: number;
  leadTimeDays?: number;
  itemGroup?: string | null;
  drawingNumber?: string | null;
  specifications?: string | null;
  requiresIncomingQC?: boolean;
  requiresFinalQC?: boolean;
  hasExpiry?: boolean;
  shelfLifeDays?: number | null;
}

interface EditingVariant {
  id: string;
  name: string;
  code: string;
  variantName?: string | null;
  variantSku?: string | null;
  variantAttributes?: Record<string, string> | null;
  purchaseRate?: number;
  saleRate?: number;
  mrp?: number;
  stock: number;
  isActive: boolean;
  bomId?: string | null;
  bomVersion?: string | null;
}

export interface VariantFormData {
  name: string;
  code: string;
  variantName: string;
  variantSku: string;
  variantAttributes: Record<string, string>;
  purchaseRate: number;
  saleRate: number;
  mrp: number;
  stock: number;
  isActive: boolean;
  bomId: string | null;
}

interface VariantFormProps {
  open: boolean;
  parentItem: ParentItem | null;
  editingVariant: EditingVariant | null;
  onClose: () => void;
  onSave: (data: VariantFormData) => void;
}

export default function VariantForm({ open, parentItem, editingVariant, onClose, onSave }: VariantFormProps) {
  const navigate = useNavigate();
  const { success } = useToast();

  const [variantName, setVariantName] = useState(editingVariant?.variantName || '');
  const [code, setCode] = useState(editingVariant?.code || '');
  const [variantSku, setVariantSku] = useState(editingVariant?.variantSku || '');
  const [attributes, setAttributes] = useState<Array<{ key: string; value: string }>>(
    editingVariant?.variantAttributes
      ? Object.entries(editingVariant.variantAttributes).map(([k, v]) => ({ key: k, value: v }))
      : []
  );
  const [purchaseRate, setPurchaseRate] = useState(
    editingVariant?.purchaseRate?.toString() ||
    parentItem?.purchaseRate?.toString() ||
    ''
  );
  const [saleRate, setSaleRate] = useState(
    editingVariant?.saleRate?.toString() ||
    parentItem?.saleRate?.toString() ||
    ''
  );
  const [mrp, setMrp] = useState(
    editingVariant?.mrp?.toString() ||
    parentItem?.mrp?.toString() ||
    ''
  );
  const [stock, setStock] = useState(editingVariant?.stock?.toString() || '0');
  const [isActive, setIsActive] = useState(editingVariant?.isActive ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !parentItem) return;

    if (editingVariant) {
      setVariantName(editingVariant.variantName || '');
      setCode(editingVariant.code || '');
      setVariantSku(editingVariant.variantSku || '');
      setAttributes(
        editingVariant.variantAttributes
          ? Object.entries(editingVariant.variantAttributes).map(([k, v]) => ({ key: k, value: v }))
          : []
      );
      setPurchaseRate(editingVariant.purchaseRate?.toString() || parentItem?.purchaseRate?.toString() || '');
      setSaleRate(editingVariant.saleRate?.toString() || parentItem?.saleRate?.toString() || '');
      setMrp(editingVariant.mrp?.toString() || parentItem?.mrp?.toString() || '');
      setStock(editingVariant.stock?.toString() || '0');
      setIsActive(editingVariant.isActive ?? true);
    } else {
      setVariantName('');
      const nextSeq = (parentItem?.variantCount || 0) + 1;
      setCode(`${parentItem?.code || 'ITM'}-${String(nextSeq).padStart(3, '0')}`);
      const autoSku = `${parentItem?.code || 'ITM'}-V${nextSeq}`;
      setVariantSku(autoSku);
      setAttributes([]);
      setPurchaseRate(parentItem?.purchaseRate?.toString() || '');
      setSaleRate(parentItem?.saleRate?.toString() || '');
      setMrp(parentItem?.mrp?.toString() || '');
      setStock('0');
      setIsActive(true);
    }
    setErrors({});
    setIsSaving(false);
  }, [open, parentItem, editingVariant]);

  const addAttribute = () => {
    setAttributes((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeAttribute = (idx: number) => {
    setAttributes((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateAttribute = (idx: number, field: 'key' | 'value', val: string) => {
    setAttributes((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, [field]: val } : a))
    );
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!variantName.trim()) e.variantName = 'Variant name is required';
    if (!code.trim()) e.code = 'Code is required';
    const sRate = parseFloat(saleRate || '0');
    const pRate = parseFloat(purchaseRate || '0');
    if (sRate > 0 && pRate > 0 && sRate < pRate) {
      e.saleRate = 'Sale rate is lower than purchase rate';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !parentItem) return;
    setIsSaving(true);

    const attrObj = attributes.reduce<Record<string, string>>((acc, { key, value }) => {
      if (key.trim()) acc[key.trim()] = value.trim();
      return acc;
    }, {});

    onSave({
      name: variantName,
      code,
      variantName,
      variantSku: variantSku || code,
      variantAttributes: attrObj,
      purchaseRate: parseFloat(purchaseRate || '0'),
      saleRate: parseFloat(saleRate || '0'),
      mrp: parseFloat(mrp || '0'),
      stock: parseInt(stock || '0', 10),
      isActive,
      bomId: editingVariant?.bomId || null,
    });
    success(editingVariant ? 'Variant updated successfully' : 'Variant added successfully');
    setIsSaving(false);
  };

  if (!open || !parentItem) return null;

  const pRateNum = parseFloat(purchaseRate || '0');
  const sRateNum = parseFloat(saleRate || '0');
  const margin = pRateNum > 0 && sRateNum > 0
    ? (((sRateNum - pRateNum) / pRateNum) * 100).toFixed(1)
    : null;

  const Field = ({ label, children, required, error }: { label: string; children: React.ReactNode; required?: boolean; error?: string }) => (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-[#1e293b] border-b border-[#e2e8f0] pb-1">{title}</h4>
      <div className="space-y-3">{children}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto h-full w-full max-w-lg bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">
              {editingVariant ? 'Edit Variant' : `Add Variant of ${parentItem?.name ?? 'Product'}`}
            </h3>
            <p className="text-xs text-[#64748b] mt-0.5">
              {editingVariant
                ? `Editing ${editingVariant?.variantName || editingVariant?.name || 'variant'}`
                : 'Create a new product variant with custom attributes'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Form Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* SECTION 1 — Inherited Fields */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <i className="ri-lock-line text-gray-400 text-sm" />
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Inherited from Parent</h4>
            </div>
            <p className="text-xs text-gray-500">
              {parentItem?.name ?? '—'} <span className="font-mono text-[10px] text-gray-400">({parentItem?.code ?? '—'})</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase">HSN Code</p>
                <p className="text-sm font-mono text-gray-700">{parentItem?.hsnCode || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase">Tax Rate</p>
                <p className="text-sm text-gray-700">{parentItem?.taxRate ?? 0}%</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase">Category</p>
                <p className="text-sm text-gray-700">{parentItem?.categoryName || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase">Item Type</p>
                <p className="text-sm text-gray-700">{(parentItem?.itemType || '—').replace(/_/g, ' ')}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase">Unit</p>
                <p className="text-sm text-gray-700">{parentItem?.unitName || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase">Tracking</p>
                <p className="text-sm text-gray-700">
                  {parentItem?.isBatchTracked ? 'Batch' : parentItem?.isSerialTracked ? 'Serial' : 'None'}
                </p>
              </div>
            </div>
          </div>

          {/* SECTION 2 — Variant Identity */}
          <Section title="Variant Identity">
            <Field label="Variant Name" required error={errors.variantName}>
              <input
                type="text" value={variantName}
                onChange={(e) => setVariantName(e.target.value)}
                placeholder="e.g. Pump X-100, 100 LPM Model"
                className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.variantName ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
              />
            </Field>

            <Field label="Variant Code" required error={errors.code}>
              <input
                type="text" value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="ITM-0303-001"
                className={`w-full h-10 px-3 rounded-lg border text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.code ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
              />
              <p className="text-xs text-[#94a3b8]">Auto-generated from parent code. You can override.</p>
            </Field>

            <Field label="Variant SKU">
              <input
                type="text" value={variantSku}
                onChange={(e) => setVariantSku(e.target.value)}
                placeholder="e.g. IP-X-100"
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
              />
              <p className="text-xs text-[#94a3b8]">Auto-generated SKU. Falls back to code if empty.</p>
            </Field>
          </Section>

          {/* SECTION 3 — Flexible Attributes */}
          <Section title="Variant Attributes">
            <div className="space-y-2">
              {attributes.map((attr, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text" value={attr.key}
                    onChange={(e) => updateAttribute(idx, 'key', e.target.value)}
                    placeholder="Attribute name (e.g. Flow Rate)"
                    className="flex-1 h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                  />
                  <input
                    type="text" value={attr.value}
                    onChange={(e) => updateAttribute(idx, 'value', e.target.value)}
                    placeholder="Value (e.g. 100 LPM)"
                    className="flex-1 h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                  />
                  <button
                    onClick={() => removeAttribute(idx)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#94a3b8] hover:text-red-500 transition-colors cursor-pointer shrink-0"
                    title="Remove attribute"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              ))}
              {attributes.length === 0 && (
                <p className="text-xs text-[#94a3b8] italic">No attributes defined yet. Click below to add.</p>
              )}
              <button
                onClick={addAttribute}
                className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-dashed border-[#cbd5e1] text-xs font-medium text-[#64748b] hover:border-[#4f46e5] hover:text-[#4f46e5] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" /> Add Attribute
              </button>
            </div>
          </Section>

          {/* SECTION 4 — Pricing */}
          <Section title="Variant Pricing">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Purchase Rate (₹)">
                <input
                  type="number" value={purchaseRate}
                  onChange={(e) => setPurchaseRate(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                />
                <p className="text-xs text-[#94a3b8]">Parent: {formatINR(parentItem?.purchaseRate ?? 0)}</p>
              </Field>
              <Field label="Sale Rate (₹)" error={errors.saleRate}>
                <input
                  type="number" value={saleRate}
                  onChange={(e) => setSaleRate(e.target.value)}
                  className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.saleRate ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                />
                <p className="text-xs text-[#94a3b8]">Parent: {formatINR(parentItem?.saleRate ?? 0)}</p>
              </Field>
            </div>
            <Field label="MRP (₹)">
              <input
                type="number" value={mrp}
                onChange={(e) => setMrp(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
              />
              <p className="text-xs text-[#94a3b8]">Parent: {formatINR(parentItem?.mrp ?? 0)}</p>
            </Field>
            {margin !== null && (
              <div className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg ${parseFloat(margin) < 0 ? 'bg-red-50 text-red-600' : parseFloat(margin) < 10 ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'}`}>
                <i className={parseFloat(margin) < 0 ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} />
                Margin: {margin}% &nbsp;|&nbsp; Profit: ₹{(sRateNum - pRateNum).toLocaleString()} per unit
              </div>
            )}
          </Section>

          {/* SECTION 5 — BOM */}
          <Section title="BOM Status">
            {editingVariant?.bomId ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-green-500 text-lg shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">BOM Linked</p>
                  <p className="text-xs text-green-600">Version {editingVariant?.bomVersion || '1.0'}</p>
                </div>
                {editingVariant && (
                  <button
                    type="button"
                    onClick={() => navigate(`/manufacturing/bom/new?variantId=${editingVariant?.id}`)}
                    className="h-7 px-3 rounded-md bg-[#4f46e5] text-xs font-medium text-white hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Update BOM
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                <i className="ri-alert-line text-amber-500 text-lg shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">No BOM — will inherit parent BOM</p>
                  <p className="text-xs text-amber-600">Create a variant-specific BOM to override parent</p>
                </div>
                {editingVariant ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/manufacturing/bom/new?variantId=${editingVariant?.id}`)}
                    className="h-7 px-3 rounded-md bg-[#4f46e5] text-xs font-medium text-white hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Create Variant BOM
                  </button>
                ) : (
                  <span className="text-xs text-amber-600 italic">Save variant first</span>
                )}
              </div>
            )}
          </Section>

          {/* SECTION 6 — Stock & Status */}
          <Section title="Stock & Status">
            <Field label="Initial Stock">
              <input
                type="number" value={stock}
                onChange={(e) => setStock(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
              />
            </Field>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                role="switch"
                aria-checked={isActive}
                onClick={() => setIsActive(!isActive)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${
                  isActive ? 'bg-[#4f46e5]' : 'bg-[#cbd5e1]'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                    isActive ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span
                onClick={() => setIsActive(!isActive)}
                className="text-sm font-medium text-[#1e293b] cursor-pointer select-none"
              >
                Active Variant
              </span>
              {isActive ? (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Active
                </span>
              ) : (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                  Inactive
                </span>
              )}
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#e2e8f0] shrink-0">
          <button onClick={onClose}
            className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={isSaving}
            className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
          >
            {isSaving ? <><i className="ri-loader-4-line animate-spin" /> Saving...</> : <><i className="ri-save-line" /> Save Variant</>}
          </button>
        </div>
      </div>
    </div>
  );
}
